'use client';

import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';

import ImageWithFallback from '@/components/common/ImageWithFallback';
import Image from 'next/image';
import { Navigation } from 'swiper/modules';
import { ContentShelf } from '@/context/global/types/homeShelf';
import CWSectionHeader from '@/context/global/components/g02/g02-d01/g02-d01-g002-cw-section-header';
import { useKaraokeStore } from '@/context/global/stores/karaokeStore';
import IconMicrophone from '@/context/global/components/Icon/IconMicrophone';

export default function CWSongRankingSection({ shelf }: { shelf: ContentShelf }) {
  const { setSelectedSong } = useKaraokeStore();
  return (
    <section className="h-auto w-full">
      <CWSectionHeader title={shelf.title} />
      <div className="h-auto w-full">
        <Swiper
          className="h-auto w-full overflow-visible"
          slidesPerView={1.5}
          spaceBetween={20}
          slidesOffsetBefore={8}
          slidesOffsetAfter={80}
          navigation={true}
          modules={[Navigation]}
          breakpoints={{
            320: {
              slidesPerView: 'auto',
              spaceBetween: 8,
              slidesOffsetBefore: 8,
              slidesOffsetAfter: 48,
            },
            640: {
              slidesPerView: 'auto',
              spaceBetween: 16,
              slidesOffsetBefore: 8,
              slidesOffsetAfter: 48,
            },
            768: {
              slidesPerView: 'auto',
              spaceBetween: 24,
              slidesOffsetBefore: 32,
              slidesOffsetAfter: 32,
            },
            1024: {
              slidesPerView: 'auto',
              spaceBetween: 24,
              slidesOffsetBefore: 32,
              slidesOffsetAfter: 32,
            },
            1350: {
              slidesPerView: 'auto',
              spaceBetween: 24,
              slidesOffsetBefore: 32,
              slidesOffsetAfter: 32,
            },
            1600: {
              slidesPerView: 'auto',
              spaceBetween: 24,
              slidesOffsetBefore: 32,
              slidesOffsetAfter: 32,
            },
            1920: {
              slidesPerView: 'auto',
              spaceBetween: 24,
              slidesOffsetBefore: 32,
              slidesOffsetAfter: 32,
            },
            2048: {
              slidesPerView: 'auto',
              spaceBetween: 24,
              slidesOffsetBefore: 32,
              slidesOffsetAfter: 32,
            },
          }}
        >
          {shelf.items.map((song, i) => (
            <SwiperSlide
              key={`song-slide-${i}-${song.id ?? 'no-id'}`}
              className="!w-[220px] md:!w-[317px]"
            >
              <div className="pl-18 relative h-fit w-full overflow-visible md:pl-[93px]">
                <div
                  className="group relative h-auto w-full cursor-pointer rounded-[8px] bg-cover bg-center transition-all duration-200 sm:rounded-[10px]"
                  onClick={() => setSelectedSong(song)}
                >
                  <div className="relative flex w-full justify-stretch p-2.5">
                    <div className="h-fit w-full">
                      <div className="flex h-full items-center">
                        <div
                          className="absolute -left-[100px] z-10 h-full w-full sm:-left-[130px]"
                          style={{ height: '100%' }}
                        >
                          <Image
                            src={`/assets/r_${i + 1}.svg`}
                            alt={`Rank ${i + 1}`}
                            fill
                            className="select-none object-contain p-2.5 !max-w-[199px]"
                          />
                        </div>

                        <div className="relative z-20 aspect-[1/1] h-full overflow-hidden rounded-[6px] transition-all duration-200 group-hover:ring-2 group-hover:ring-[#b2d234] group-hover:shadow-[0_0_14px_rgba(178,210,52,0.55)] sm:rounded-[8px]">
                          <ImageWithFallback
                            src={song.coverUrl}
                            alt={song.title || 'cover'}
                            className="h-full !max-w-[200px] rounded-[6px] object-cover sm:rounded-[8px]"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex w-full items-center pb-1.5">
                    <div className="flex w-full flex-col items-center pb-1.5">
                      <div className="w-[90%] truncate text-[22px] font-bold text-white md:text-[24px]">
                        {song.title || `Song Title ${i + 1}`}
                      </div>
                      <div className="mt-[-8px] w-[90%] truncate text-sm font-medium text-[#B5B5B5] md:text-lg">
                        {song.artist || 'Artist Name'}
                      </div>
                    </div>
                    <div className="-ml-10 flex items-center gap-1.5 pt-5 md:pr-4">
                      <IconMicrophone className="size-4 text-[#B5B5B5]" />
                      <p className="text-[14px] text-[#B5B5B5] md:text-[18px] lg:text-[20px]">
                        {song.numOfListener || 0}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
}
